import type { ServicePageData } from "@/components/sections/service-page";
import {
  Zap, Shield, Rocket, Code, Cloud, Database, Users, TrendingUp, Search,
  Mail, MessageSquare, BarChart3, Palette, Workflow, Cpu, ShoppingCart,
  Wrench, Lock, Globe, Package, Calendar, GraduationCap, Store, Layers,
  Target, LineChart, Sparkles, Server, HelpCircle,
} from "lucide-react";

const stockProcess = [
  { step: "01", title: "Discovery", description: "Understand goals, constraints and success metrics." },
  { step: "02", title: "Plan", description: "Scope, timeline, milestones and approvals." },
  { step: "03", title: "Build", description: "Iterative delivery with weekly demos and feedback." },
  { step: "04", title: "Launch", description: "QA, deployment, training and post-launch support." },
];

const stockPricing = [
  { name: "Starter", price: "$1.5k", features: ["Core scope", "2-week delivery", "Email support", "Post-launch fixes"] },
  { name: "Growth", price: "$5k", period: "project", highlighted: true, features: ["Full scope", "4–6 weeks", "Priority support", "Analytics setup"] },
  { name: "Enterprise", price: "Custom", features: ["Bespoke build", "Dedicated team", "SLA + training", "Ongoing partnership"] },
];

const stockFaqs = [
  { q: "How long does a typical engagement take?", a: "Most projects ship in 4–8 weeks; enterprise builds run 3–6 months with staged releases." },
  { q: "Do you support post-launch?", a: "Yes — every project includes 30 days of free fixes plus optional maintenance retainers." },
  { q: "Can you work with our existing team?", a: "Absolutely. We integrate with your engineers, designers or agency stakeholders on any tool." },
  { q: "What technologies do you use?", a: "Modern, proven stacks: React/Next, TypeScript, Node, PostgreSQL, and cloud-native infra on AWS/GCP/Cloudflare." },
];

const make = (partial: Partial<ServicePageData> & Pick<ServicePageData, "eyebrow" | "title" | "sub" | "intro" | "features">): ServicePageData => ({
  process: stockProcess,
  deliverables: [
    "Discovery workshop and scoping doc",
    "Design mockups and prototype",
    "Fully built and tested solution",
    "Deployment and go-live support",
    "Team training and documentation",
    "30 days of post-launch fixes",
  ],
  pricing: stockPricing,
  faqs: stockFaqs,
  ...partial,
});

export const pageData: Record<string, ServicePageData> = {
  // ── Services ─────────────────────────────────────────────
  "saas-development": make({
    eyebrow: "Services", title: "SaaS Development",
    sub: "Multi-tenant SaaS products, built to scale.",
    intro: "From idea to production SaaS — multi-tenant architecture, subscription billing, role-based access, admin portals and analytics. We ship MVPs in 6 weeks and grow them into revenue engines.",
    features: [
      { icon: Layers, title: "Multi-tenant architecture", description: "Isolated data per tenant with shared infra for cost efficiency." },
      { icon: Users, title: "Auth & RBAC", description: "SSO, magic links, granular roles and audit logs." },
      { icon: TrendingUp, title: "Subscription billing", description: "Stripe / Paddle billing with trials, upgrades and dunning." },
      { icon: Cloud, title: "Cloud-native", description: "Auto-scaling on AWS, GCP or Cloudflare Workers." },
      { icon: BarChart3, title: "Product analytics", description: "PostHog / Amplitude wired to every core event." },
      { icon: Shield, title: "SOC 2 ready", description: "Audit-ready logs, backups and access controls from day one." },
    ],
  }),
  "ai-automation": make({
    eyebrow: "Services", title: "AI Automation",
    sub: "LLM workflows, agents and business automation.",
    intro: "Automate the busywork. We build GPT-powered assistants, document extractors, customer-support agents and workflow bots that plug into your existing tools.",
    features: [
      { icon: Sparkles, title: "Custom AI agents", description: "OpenAI, Anthropic and open-source models tuned to your data." },
      { icon: Workflow, title: "Workflow automation", description: "n8n, Zapier and custom pipelines that replace manual work." },
      { icon: Database, title: "RAG over your data", description: "Vector search on your docs, tickets and CRM." },
      { icon: MessageSquare, title: "AI chatbots", description: "Website, WhatsApp and Slack bots trained on your knowledge." },
      { icon: Cpu, title: "Model fine-tuning", description: "Domain-specific fine-tunes for higher accuracy and lower cost." },
      { icon: Zap, title: "Fast prototypes", description: "Working prototype in 2 weeks — no long research phase." },
    ],
  }),
  "ecommerce": make({
    eyebrow: "Services", title: "E-commerce Development",
    sub: "Shopify, WooCommerce and headless commerce.",
    intro: "Online stores that convert — from single-brand DTC to multi-vendor marketplaces. Shopify, WooCommerce, Medusa and fully custom headless builds.",
    features: [
      { icon: ShoppingCart, title: "Store setup", description: "Shopify, WooCommerce or headless with your branding." },
      { icon: Package, title: "Product ops", description: "Bulk import, variants, inventory sync and pricing rules." },
      { icon: TrendingUp, title: "CRO built in", description: "Optimised checkout, upsells, abandoned-cart flows." },
      { icon: Globe, title: "Multi-region", description: "Currency, tax, shipping and language for every market." },
      { icon: Cloud, title: "Headless storefront", description: "Next.js / Hydrogen fronts for lightning-fast pages." },
      { icon: Lock, title: "Secure payments", description: "Stripe, PayPal, Klarna, Apple/Google Pay integrated." },
    ],
  }),
  "devops": make({
    eyebrow: "Services", title: "DevOps",
    sub: "Cloud infra, CI/CD and observability.",
    intro: "Ship faster with zero drama. We set up CI/CD pipelines, infrastructure-as-code, monitoring and on-call so your team can deploy 10x a day with confidence.",
    features: [
      { icon: Cloud, title: "Cloud architecture", description: "AWS, GCP, Azure — designed for cost and reliability." },
      { icon: Workflow, title: "CI/CD pipelines", description: "GitHub Actions, GitLab CI, Argo, Buildkite." },
      { icon: Code, title: "Infrastructure-as-code", description: "Terraform, Pulumi, Kubernetes manifests." },
      { icon: BarChart3, title: "Observability", description: "Datadog, Grafana, Sentry — logs, metrics, traces." },
      { icon: Shield, title: "Security & compliance", description: "IAM, secrets, WAF, SOC 2 / ISO scaffolding." },
      { icon: Rocket, title: "Zero-downtime deploys", description: "Blue/green, canary and progressive rollouts." },
    ],
  }),
  "api-integration": make({
    eyebrow: "Services", title: "API Integration",
    sub: "Connect anything to anything.",
    intro: "Stripe, HubSpot, Salesforce, QuickBooks, Twilio, WhatsApp, WHMCS — whatever you need connected, we wire it up with resilient, observable integrations.",
    features: [
      { icon: Zap, title: "Webhooks & events", description: "Reliable event pipelines with retries and dead-letter queues." },
      { icon: Database, title: "Data sync", description: "Two-way sync between your CRM, ERP, ecommerce and warehouse." },
      { icon: Lock, title: "Secure auth", description: "OAuth2, JWT, HMAC signatures and secret rotation." },
      { icon: Workflow, title: "Middleware layer", description: "Custom API gateway to normalise upstream providers." },
      { icon: BarChart3, title: "Monitoring", description: "Per-integration dashboards and alerting on failures." },
      { icon: Code, title: "SDKs & docs", description: "Typed client SDKs and OpenAPI-driven documentation." },
    ],
  }),
  "maintenance": make({
    eyebrow: "Services", title: "Website & Software Maintenance",
    sub: "Ongoing care for the sites and apps you own.",
    intro: "Keep your website and software fast, secure and up to date without hiring a full-time team. Monthly retainers with SLAs and monthly reports.",
    features: [
      { icon: Wrench, title: "Updates & patches", description: "Framework, plugin and dependency updates monthly." },
      { icon: Shield, title: "Security monitoring", description: "Malware scans, WAF rules and vulnerability alerts." },
      { icon: Database, title: "Backups & restore", description: "Daily backups with tested restore procedures." },
      { icon: Zap, title: "Performance tuning", description: "Page speed, Core Web Vitals and DB query tuning." },
      { icon: BarChart3, title: "Monthly reports", description: "Uptime, changes, incidents and recommendations." },
      { icon: Users, title: "Dedicated engineer", description: "Same engineer every month — they learn your stack." },
    ],
  }),

  // ── Marketing ────────────────────────────────────────────
  "content-marketing": make({
    eyebrow: "Marketing", title: "Content Marketing",
    sub: "SEO content that ranks and converts.",
    intro: "We plan, write and publish content that ranks in Google and moves readers to action — blogs, landing pages, whitepapers and video scripts.",
    features: [
      { icon: Search, title: "Keyword strategy", description: "Topic clusters mapped to buyer intent." },
      { icon: Palette, title: "Editorial calendar", description: "Weekly cadence with briefs, drafts and reviews." },
      { icon: TrendingUp, title: "SEO-optimised writing", description: "On-page SEO, schema and internal linking done right." },
      { icon: Users, title: "Expert writers", description: "Subject-matter writers for tech, SaaS, health and finance." },
      { icon: BarChart3, title: "Performance reporting", description: "Rankings, traffic and pipeline attribution monthly." },
      { icon: Sparkles, title: "AI-assisted, human-edited", description: "Faster drafts, human polish, zero AI-detector risk." },
    ],
  }),
  "whatsapp-sms-marketing": make({
    eyebrow: "Marketing", title: "WhatsApp & SMS Marketing",
    sub: "Reach customers where they read messages.",
    intro: "Broadcast campaigns, drip flows and 2-way conversations on WhatsApp Business API and SMS — with opt-in management and deliverability best practices.",
    features: [
      { icon: MessageSquare, title: "WhatsApp Business API", description: "Official templates, catalogs and chatbots." },
      { icon: Mail, title: "SMS campaigns", description: "Broadcasts, drip flows and transactional messages." },
      { icon: Users, title: "Contact segmentation", description: "Segments by tag, behaviour, purchase history." },
      { icon: Workflow, title: "Automation flows", description: "Cart-abandonment, welcome, win-back and NPS flows." },
      { icon: BarChart3, title: "Analytics", description: "Delivery, read, click and conversion per campaign." },
      { icon: Shield, title: "Compliance", description: "Opt-in, opt-out, DND lists and regional rules handled." },
    ],
  }),
  "conversion-optimization": make({
    eyebrow: "Marketing", title: "Conversion Rate Optimization",
    sub: "Turn more of your traffic into revenue.",
    intro: "Structured CRO — analytics, heatmaps, session recordings, hypotheses, A/B tests. Typically 20–60% lifts in the first quarter.",
    features: [
      { icon: Target, title: "Conversion audit", description: "End-to-end funnel audit with prioritised opportunities." },
      { icon: LineChart, title: "A/B testing", description: "Statistically sound experiments on VWO / Optimizely / GA." },
      { icon: Users, title: "User research", description: "Heatmaps, recordings and 5-user tests every sprint." },
      { icon: Palette, title: "Landing page design", description: "New landers built and tested against control." },
      { icon: Zap, title: "Checkout optimisation", description: "Cut friction, add trust signals, lift AOV." },
      { icon: BarChart3, title: "Monthly reporting", description: "Lift, revenue impact, learnings and next hypotheses." },
    ],
  }),
  "analytics-tracking": make({
    eyebrow: "Marketing", title: "Analytics & Tracking Setup",
    sub: "GA4, GTM, server-side tracking done right.",
    intro: "Get accurate data you can trust — GA4, GTM, Meta CAPI, TikTok Events, server-side tracking, consent mode and dashboards.",
    features: [
      { icon: BarChart3, title: "GA4 + GTM", description: "Full ecommerce tracking with custom events." },
      { icon: Server, title: "Server-side tracking", description: "sGTM on Cloudflare / GCP for iOS 14 accuracy." },
      { icon: Shield, title: "Consent mode v2", description: "GDPR-compliant consent banners and signal handling." },
      { icon: Target, title: "Conversion APIs", description: "Meta CAPI, TikTok, Google Ads Enhanced Conversions." },
      { icon: LineChart, title: "Dashboards", description: "Looker Studio / Metabase dashboards for CMOs and CEOs." },
      { icon: Workflow, title: "Attribution", description: "Multi-touch attribution across paid, organic and email." },
    ],
  }),
  "branding-creative-design": make({
    eyebrow: "Marketing", title: "Branding & Creative Design",
    sub: "Brand identity, ads creative and design systems.",
    intro: "Distinctive brand identity and creative that stops the scroll — logos, guidelines, ad creative, decks and social templates.",
    features: [
      { icon: Palette, title: "Brand identity", description: "Logo, colour, type, voice and usage guidelines." },
      { icon: Sparkles, title: "Ad creative", description: "Static and video creative optimised per platform." },
      { icon: Layers, title: "Design systems", description: "Figma libraries your team can extend forever." },
      { icon: Globe, title: "Web & app design", description: "High-conversion UI that stays on-brand." },
      { icon: Users, title: "Pitch decks", description: "Investor and sales decks that close." },
      { icon: TrendingUp, title: "Social templates", description: "Reusable templates for Instagram, LinkedIn, TikTok." },
    ],
  }),

  // ── Software ─────────────────────────────────────────────
  "crm": make({
    eyebrow: "Software", title: "CRM Software Development",
    sub: "Sales, marketing and support in one system.",
    intro: "Custom CRMs that fit how your team actually sells — pipeline management, email sequences, call logging, quotes and support tickets.",
    features: [
      { icon: Users, title: "Contact & company records", description: "Single source of truth with custom fields." },
      { icon: TrendingUp, title: "Sales pipeline", description: "Drag-and-drop deals, forecasting and quotas." },
      { icon: Mail, title: "Email sequences", description: "Multi-step outreach with tracking and replies." },
      { icon: MessageSquare, title: "Support ticketing", description: "Shared inbox, SLAs and CSAT surveys." },
      { icon: Workflow, title: "Automation", description: "Triggers, tasks, notifications and hand-offs." },
      { icon: BarChart3, title: "Reporting", description: "Pipeline, activity, revenue and cohort reports." },
    ],
  }),
  "lms": make({
    eyebrow: "Software", title: "LMS Software Development",
    sub: "Sell courses, certify learners, manage teams.",
    intro: "Full-featured LMS for course creators, universities and enterprise L&D — video, quizzes, certificates, cohorts and gamification.",
    features: [
      { icon: GraduationCap, title: "Course builder", description: "Modules, lessons, video, PDFs and downloads." },
      { icon: Users, title: "Cohorts & groups", description: "Cohort-based courses with discussion and live sessions." },
      { icon: Shield, title: "Certificates", description: "Auto-generated verifiable certificates on completion." },
      { icon: TrendingUp, title: "Progress tracking", description: "Learner dashboards and manager reporting." },
      { icon: ShoppingCart, title: "Sell courses", description: "Stripe, PayPal, coupons, subscriptions and bundles." },
      { icon: Sparkles, title: "AI tutor", description: "Optional GPT tutor answering questions from your content." },
    ],
  }),
  "accounting": make({
    eyebrow: "Software", title: "Accounting Software Development",
    sub: "Invoicing, expenses, tax and reporting.",
    intro: "Custom accounting systems for regulated markets — invoicing, expenses, tax filing (VAT, ZATCA, GST), multi-currency and audit trails.",
    features: [
      { icon: BarChart3, title: "Chart of accounts", description: "Double-entry accounting with your COA." },
      { icon: Mail, title: "Invoicing", description: "Branded invoices, recurring billing and reminders." },
      { icon: TrendingUp, title: "Expense tracking", description: "Receipts, categories, approvals and reimbursements." },
      { icon: Shield, title: "Tax compliance", description: "VAT, ZATCA e-invoicing, GST, IRS forms." },
      { icon: Globe, title: "Multi-currency", description: "FX rates, gain/loss and consolidated reporting." },
      { icon: Database, title: "Audit trail", description: "Immutable ledger with role-based access." },
    ],
  }),
  "inventory": make({
    eyebrow: "Software", title: "Inventory Software Development",
    sub: "Stock, warehouses and purchase orders.",
    intro: "Real-time inventory across warehouses, channels and POS — barcode scanning, PO management, low-stock alerts and forecasting.",
    features: [
      { icon: Package, title: "Multi-warehouse", description: "Stock levels across every location in real time." },
      { icon: Zap, title: "Barcode & scanning", description: "Mobile scanning for receiving, picking and stock-take." },
      { icon: Workflow, title: "Purchase orders", description: "Auto-reorder from suppliers based on min stock." },
      { icon: TrendingUp, title: "Demand forecasting", description: "AI-powered forecasts to prevent stock-outs." },
      { icon: ShoppingCart, title: "Channel sync", description: "Sync stock to Shopify, Amazon, POS and marketplaces." },
      { icon: BarChart3, title: "Inventory reports", description: "Turnover, dead stock, valuation and margin reports." },
    ],
  }),
  "booking": make({
    eyebrow: "Software", title: "Booking Software Development",
    sub: "Appointments, resources and payments.",
    intro: "Booking platforms for salons, clinics, hotels, coaches and rentals — calendar, deposits, reminders, cancellations and staff management.",
    features: [
      { icon: Calendar, title: "Smart calendar", description: "Staff, room and resource calendars in one view." },
      { icon: MessageSquare, title: "Reminders", description: "Email, SMS and WhatsApp reminders with confirmations." },
      { icon: ShoppingCart, title: "Online payments", description: "Deposits, full payment, packages and memberships." },
      { icon: Users, title: "Customer accounts", description: "Booking history, preferences and loyalty points." },
      { icon: Workflow, title: "Cancellation policies", description: "Configurable rules with automatic refunds." },
      { icon: BarChart3, title: "Utilisation reports", description: "Staff utilisation, no-shows and revenue reports." },
    ],
  }),
  "marketplace": make({
    eyebrow: "Software", title: "Marketplace Software Development",
    sub: "Multi-vendor platforms with commissions.",
    intro: "Two-sided marketplaces like Airbnb, Fiverr or Amazon — vendor onboarding, listings, escrow payments, reviews and disputes.",
    features: [
      { icon: Store, title: "Vendor onboarding", description: "KYC, storefronts and vendor dashboards." },
      { icon: Package, title: "Listings & search", description: "Rich search, filters and personalisation." },
      { icon: ShoppingCart, title: "Escrow payments", description: "Stripe Connect / Adyen with split payouts." },
      { icon: MessageSquare, title: "Chat & disputes", description: "Buyer-seller chat with dispute resolution flow." },
      { icon: TrendingUp, title: "Commission engine", description: "Flexible commission, tiered fees, promotions." },
      { icon: Shield, title: "Trust & safety", description: "Reviews, moderation, fraud detection and refunds." },
    ],
  }),

  // ── Hosting ──────────────────────────────────────────────
  "ssl": make({
    eyebrow: "Hosting", title: "SSL Certificates",
    sub: "Free & paid SSL for every website.",
    intro: "Encrypt every visitor connection with SSL — free Let's Encrypt on all our hosting plans, plus premium DV, OV and EV certificates for regulated businesses.",
    features: [
      { icon: Lock, title: "Free Let's Encrypt", description: "Auto-issued and auto-renewing on every plan." },
      { icon: Shield, title: "DV, OV & EV", description: "Sectigo, DigiCert and GlobalSign certificates." },
      { icon: Globe, title: "Wildcard & multi-domain", description: "One cert for every subdomain or domain you own." },
      { icon: Zap, title: "1-click install", description: "Auto-installed on cPanel and our managed hosting." },
      { icon: TrendingUp, title: "SEO boost", description: "HTTPS is a Google ranking signal." },
      { icon: Users, title: "24/7 support", description: "Issuance help, validation and troubleshooting." },
    ],
    pricing: [
      { name: "Free SSL", price: "$0", features: ["Let's Encrypt", "Auto-renewing", "Included on hosting", "Domain validated"] },
      { name: "Positive SSL", price: "$15", period: "year", highlighted: true, features: ["Sectigo DV", "$50k warranty", "99.9% browser trust", "Site seal"] },
      { name: "Wildcard SSL", price: "$79", period: "year", features: ["Unlimited subdomains", "$250k warranty", "Priority issuance", "24/7 support"] },
    ],
  }),
  "whois": make({
    eyebrow: "Hosting", title: "WHOIS Lookup",
    sub: "Look up domain ownership and registration details.",
    intro: "Free WHOIS lookup for any domain — see registrar, expiry, nameservers and contact info (where public). Bulk lookup and API access available.",
    features: [
      { icon: Search, title: "Instant lookup", description: "Domain owner, registrar and expiry in seconds." },
      { icon: Globe, title: "All TLDs", description: "gTLDs, ccTLDs and new TLDs supported." },
      { icon: Shield, title: "Privacy protection", description: "Turn on WHOIS privacy for domains you own with us." },
      { icon: Zap, title: "Bulk lookup", description: "Upload a CSV to check hundreds of domains at once." },
      { icon: Code, title: "WHOIS API", description: "REST API for developers and monitoring tools." },
      { icon: HelpCircle, title: "Expiry monitoring", description: "Alerts before your domains expire." },
    ],
  }),
  "whmcs": make({
    eyebrow: "Billing", title: "WHMCS Client Portal",
    sub: "Billing, invoices, domains and hosting — all in one portal.",
    intro: "Our WHMCS-powered client area lets you manage services, pay invoices, renew domains, open support tickets and track orders in real time. Fully integrated with our hosting, domains and payment gateways.",
    features: [
      { icon: Users, title: "Unified client area", description: "One login for services, domains, invoices and tickets." },
      { icon: TrendingUp, title: "Automated billing", description: "Recurring invoices, dunning and receipts — hands-off." },
      { icon: Globe, title: "Domain management", description: "Register, transfer, renew and manage DNS from one place." },
      { icon: Server, title: "Hosting provisioning", description: "cPanel / VPS auto-provisioned the moment payment clears." },
      { icon: Shield, title: "Secure payments", description: "Stripe, PayTabs, HyperPay, SSLCommerz and more — PCI-safe." },
      { icon: HelpCircle, title: "Support tickets", description: "Priority ticketing with SLAs and knowledge base search." },
    ],
    pricing: [
      { name: "Client Portal", price: "Free", features: ["Included with any service", "Invoices & receipts", "Domain / hosting mgmt", "Support tickets"] },
      { name: "Priority Support", price: "$29", period: "month", highlighted: true, features: ["1-hour response SLA", "Priority queue", "Phone & chat", "Managed onboarding"] },
      { name: "Enterprise", price: "Custom", features: ["Dedicated manager", "Custom SLA", "White-label portal", "API access"] },
    ],
  }),
};
